import { type DataViewI18nKey, dataViewI18nT } from '../core/i18n.js';
import type { DataViewDataType } from '../core/view/data-view.js';

export const TEMPORAL_PROFILE_VIEW_TYPES = ['calendar', 'gantt'] as const;

export type TemporalProfileViewType =
  (typeof TEMPORAL_PROFILE_VIEW_TYPES)[number];

export type TemporalProfileRole =
  | 'title'
  | 'start'
  | 'end'
  | 'allDay'
  | 'timezone'
  | 'calendarId'
  | 'status'
  | 'visibility'
  | 'attendees'
  | 'assignee'
  | 'progress'
  | 'dependencies'
  | 'locationPhysical'
  | 'locationVirtual'
  | 'description'
  | 'reminder'
  | 'recurrence'
  | 'recurrenceExdate'
  | 'recurrenceId';

export const TEMPORAL_PROFILE_ROLES: readonly TemporalProfileRole[] = [
  'title',
  'start',
  'end',
  'allDay',
  'timezone',
  'calendarId',
  'status',
  'visibility',
  'attendees',
  'assignee',
  'progress',
  'dependencies',
  'locationPhysical',
  'locationVirtual',
  'description',
  'reminder',
  'recurrence',
  'recurrenceExdate',
  'recurrenceId',
];

export const TEMPORAL_PROFILE_ROLE_FIELDS = {
  allDay: ['allDayCol'],
  assignee: ['assigneeCol'],
  attendees: ['attendeesCol'],
  calendarId: ['calendarIdCol'],
  dependencies: ['dependenciesCol'],
  description: ['descriptionCol'],
  end: ['endDateCol'],
  locationPhysical: ['locationPhysicalCol'],
  locationVirtual: ['locationVirtualCol'],
  progress: ['progressCol'],
  recurrence: ['recurrenceCol'],
  recurrenceExdate: ['recurrenceExdateCol'],
  recurrenceId: ['recurrenceIdCol'],
  reminder: ['reminderCol'],
  start: ['startDateCol'],
  status: ['statusCol'],
  timezone: ['timezoneCol'],
  title: ['header.titleColumn'],
  visibility: ['visibilityCol'],
} satisfies Record<TemporalProfileRole, readonly string[]>;

export const TEMPORAL_PROFILE_ROLE_PRIMARY_TYPE = {
  allDay: 'checkbox',
  assignee: 'member',
  attendees: 'rich-text',
  calendarId: 'select',
  dependencies: 'relation',
  description: 'rich-text',
  end: 'date',
  locationPhysical: 'rich-text',
  locationVirtual: 'link',
  progress: 'progress',
  recurrence: 'rich-text',
  recurrenceExdate: 'rich-text',
  recurrenceId: 'rich-text',
  reminder: 'rich-text',
  start: 'date',
  status: 'select',
  timezone: 'rich-text',
  title: 'title',
  visibility: 'select',
} satisfies Record<TemporalProfileRole, string>;

const TEMPORAL_PROFILE_NAME_PATTERNS = {
  allDay: /\b(all.?day|ganztag)\b/i,
  assignee: /\b(assign(ee|ed)?|owner|responsible|lead|user|member|person)\b/i,
  attendees: /\b(attendees|guests|invite|teilnehmer)\b/i,
  calendarId: /\b(calendar|kalender|resource)\b/i,
  dependencies:
    /\b(depend(enc(y|ies))?|block(ed|s|ing)?|predecessor|successor|requires)\b/i,
  description: /\b(description|notes|details|beschreibung)\b/i,
  end: /\b(end|finish|due|to|deadline|target)\b/i,
  locationPhysical: /\b(address|ort|location|place)\b/i,
  locationVirtual: /\b(zoom|teams|meet|link|url|virtual)\b/i,
  progress: /\b(progress|percent|complete|completion|done|%)\b/i,
  recurrence: /\b(recurr|repeat|rrule|wiederhol)\b/i,
  recurrenceExdate: /\b(exdate|exception|ausnahme)\b/i,
  recurrenceId: /\b(recurrence.?id|recurrence-id)\b/i,
  reminder: /\b(reminder|alert|erinner)\b/i,
  start: /\b(start|begin|from|kick.?off)\b/i,
  status: /\b(status|state|phase|stage|zustand)\b/i,
  timezone: /\b(time.?zone|tz|zeitzone)\b/i,
  title: /\b(title|name|titel)\b/i,
  visibility: /\b(visibility|sichtbarkeit)\b/i,
} satisfies Record<TemporalProfileRole, RegExp>;

const TEMPORAL_PROFILE_STATIC_NAMES = {
  assignee: 'Assignee',
  dependencies: 'Dependencies',
  progress: 'Progress',
} as const;

type TemporalProfileDataSource = {
  getAllPropertyIds(): string[];
  propertyDataGet?(propertyId: string): Record<string, unknown>;
  propertyNameGet(propertyId: string): string;
  propertyTypeGet(propertyId: string): string | undefined;
  viewDataList$?: { value: readonly DataViewDataType[] };
};

export function isTemporalProfileViewType(
  type: string | undefined
): type is TemporalProfileViewType {
  return type === 'calendar' || type === 'gantt';
}

export function temporalProfileRoleColumnId(
  viewData: DataViewDataType | undefined,
  role: TemporalProfileRole
): string | undefined {
  if (!viewData || !isTemporalProfileViewType(viewData.mode)) return undefined;
  for (const field of TEMPORAL_PROFILE_ROLE_FIELDS[role]) {
    const value = readPath(
      viewData as unknown as Record<string, unknown>,
      field
    );
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return undefined;
}

export function temporalProfileRoleAcceptsPropertyType(
  role: TemporalProfileRole,
  propertyType: string | undefined,
  propertyData?: Record<string, unknown>,
  selfBlockId?: string
): boolean {
  if (!propertyType) return false;
  if (role === 'title') return propertyType === 'title';
  if (role === 'start' || role === 'end') return propertyType === 'date';
  if (role === 'allDay') return propertyType === 'checkbox';
  if (role === 'assignee') return propertyType === 'member';
  if (role === 'progress') return propertyType === 'progress';
  if (role === 'dependencies') {
    if (propertyType !== 'relation') return false;
    if (!selfBlockId) return true;
    return propertyData?.targetDatabaseId === selfBlockId;
  }
  if (role === 'calendarId' || role === 'status' || role === 'visibility') {
    return propertyType === 'select' || propertyType === 'rich-text';
  }
  if (role === 'locationVirtual') {
    return propertyType === 'link' || propertyType === 'rich-text';
  }
  return propertyType === 'rich-text';
}

export function defaultTemporalProfileColumnName(
  role: TemporalProfileRole
): string {
  if (role in TEMPORAL_PROFILE_STATIC_NAMES) {
    return TEMPORAL_PROFILE_STATIC_NAMES[
      role as keyof typeof TEMPORAL_PROFILE_STATIC_NAMES
    ];
  }
  return dataViewI18nT(`calendar.wizard.role.${role}.name` as DataViewI18nKey);
}

export function resolveTemporalProfileColumn(input: {
  currentId?: string;
  dataSource: TemporalProfileDataSource;
  excludeIds?: readonly string[];
  role: TemporalProfileRole;
  selfBlockId?: string;
}): string | undefined {
  const { currentId, dataSource, role, selfBlockId } = input;
  const excludeIds = new Set(input.excludeIds ?? []);
  const existing = new Set(dataSource.getAllPropertyIds());

  if (
    isUsableTemporalProfileColumn(dataSource, role, currentId, existing, {
      excludeIds,
      selfBlockId,
    })
  ) {
    return currentId;
  }

  for (const viewData of dataSource.viewDataList$?.value ?? []) {
    const propertyId = temporalProfileRoleColumnId(viewData, role);
    if (
      isUsableTemporalProfileColumn(dataSource, role, propertyId, existing, {
        excludeIds,
        selfBlockId,
      })
    ) {
      return propertyId;
    }
  }

  return dataSource.getAllPropertyIds().find(propertyId => {
    if (excludeIds.has(propertyId)) return false;
    if (
      !temporalProfileRoleAcceptsPropertyType(
        role,
        dataSource.propertyTypeGet(propertyId),
        dataSource.propertyDataGet?.(propertyId),
        selfBlockId
      )
    ) {
      return false;
    }
    return TEMPORAL_PROFILE_NAME_PATTERNS[role].test(
      dataSource.propertyNameGet(propertyId)
    );
  });
}

function isUsableTemporalProfileColumn(
  dataSource: TemporalProfileDataSource,
  role: TemporalProfileRole,
  propertyId: string | undefined,
  existing: ReadonlySet<string>,
  options: {
    excludeIds: ReadonlySet<string>;
    selfBlockId?: string;
  }
): propertyId is string {
  if (!propertyId || !existing.has(propertyId)) return false;
  if (options.excludeIds.has(propertyId)) return false;
  return temporalProfileRoleAcceptsPropertyType(
    role,
    dataSource.propertyTypeGet(propertyId),
    dataSource.propertyDataGet?.(propertyId),
    options.selfBlockId
  );
}

function readPath(source: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((value, key) => {
    if (!value || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[key];
  }, source);
}
